/**
 * Base API Client Configuration
 *
 * Centralized Axios-based HTTP client with:
 * - Request/Response Interceptors
 * - Automatic Token Management
 * - CSRF Protection with Double Submit Cookie Pattern
 * - Retry Logic with Exponential Backoff
 * - Standardized Error Handling
 */

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { getCookie } from '../utils/cookies';
import { logger } from '../utils/logger';

/**
 * Check if CSRF debug mode is enabled
 * Enabled in development or when VITE_CSRF_DEBUG is set to 'true'
 */
const CSRF_DEBUG = import.meta.env.DEV || import.meta.env.VITE_CSRF_DEBUG === 'true';

/**
 * Debug logging helper for CSRF operations
 */
function csrfLog(message: string, data?: any) {
  if (CSRF_DEBUG) {
    logger.log(message, data !== undefined ? data : '');
  }
}

/**
 * Error logging helper (only logs in development)
 */
function csrfError(message: string, error?: any) {
  logger.error(message, error !== undefined ? error : '');
}

/**
 * API Client Configuration Options
 */
export interface ApiClientConfig {
  baseURL?: string;
  timeout?: number;
  retryAttempts?: number;
  retryDelay?: number;
}

/**
 * Standardized API Error Response
 */
export interface ApiErrorResponse {
  error: string;
  message: string;
  statusCode: number;
  timestamp?: string;
  path?: string;
  details?: Array<{
    field: string;
    message: string;
    constraint?: string;
  }>;
}

/**
 * Error Categories for Specialized Handling
 */
export enum ErrorCategory {
  NETWORK = 'NETWORK_ERROR',
  VALIDATION = 'VALIDATION_ERROR',
  AUTHENTICATION = 'AUTHENTICATION_ERROR',
  AUTHORIZATION = 'AUTHORIZATION_ERROR',
  SERVER = 'SERVER_ERROR',
  CLIENT = 'CLIENT_ERROR',
  TIMEOUT = 'TIMEOUT_ERROR',
  UNKNOWN = 'UNKNOWN_ERROR',
}

/**
 * Categorized API Error
 */
export class ApiError extends Error {
  constructor(
    public readonly category: ErrorCategory,
    public readonly statusCode: number | null,
    public readonly response: ApiErrorResponse | null,
    message: string,
    public readonly originalError?: Error
  ) {
    super(message);
    this.name = 'ApiError';
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  /**
   * Check if error is retryable
   */
  isRetryable(): boolean {
    return (
      this.category === ErrorCategory.NETWORK ||
      this.category === ErrorCategory.TIMEOUT ||
      (this.category === ErrorCategory.SERVER && this.statusCode !== null && this.statusCode >= 500)
    );
  }
}

/**
 * Circuit Breaker State
 */
class CircuitBreaker {
  private failureCount = 0;
  private lastFailureTime: number | null = null;
  private state: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED';

  private readonly FAILURE_THRESHOLD = 5;
  private readonly RECOVERY_TIMEOUT = 60000; // 60 seconds

  recordSuccess(): void {
    this.failureCount = 0;
    this.state = 'CLOSED';
  }

  recordFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.failureCount >= this.FAILURE_THRESHOLD) {
      this.state = 'OPEN';
    }
  }

  canAttempt(): boolean {
    if (this.state === 'CLOSED') {
      return true;
    }

    if (this.state === 'OPEN') {
      const now = Date.now();
      if (this.lastFailureTime && now - this.lastFailureTime >= this.RECOVERY_TIMEOUT) {
        this.state = 'HALF_OPEN';
        return true;
      }
      return false;
    }

    // HALF_OPEN state - allow one attempt
    return true;
  }

  isOpen(): boolean {
    return this.state === 'OPEN';
  }
}

/**
 * Token Refresh State Management
 */
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeTokenRefresh(callback: (token: string) => void): void {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(token: string): void {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
}

/**
 * Base API Client Class
 */
export class ApiClient {
  private axiosInstance: AxiosInstance;
  private config: Required<ApiClientConfig>;
  private circuitBreaker: CircuitBreaker;

  constructor(config: ApiClientConfig = {}) {
    this.config = {
      baseURL: config.baseURL || import.meta.env.VITE_API_BASE_URL ||
        (import.meta.env.PROD ? 'https://api.99fingers.com/api/v1' : 'http://localhost:3000/api/v1'),
      timeout: config.timeout || 10000,
      retryAttempts: config.retryAttempts || 3,
      retryDelay: config.retryDelay || 500,
    };

    this.circuitBreaker = new CircuitBreaker();

    csrfLog('[CSRF DEBUG] API Client initializing...', {
      apiBaseUrl: this.config.baseURL,
      mode: import.meta.env.MODE,
      prod: import.meta.env.PROD,
      csrfDebug: CSRF_DEBUG
    });

    this.axiosInstance = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      withCredentials: true, // Required for CSRF cookies
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupRequestInterceptors();
    this.setupResponseInterceptors();

    csrfLog('[CSRF DEBUG] API Client initialized successfully');
  }

  /**
   * Setup Request Interceptors
   */
  private setupRequestInterceptors(): void {
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Check circuit breaker
        if (this.circuitBreaker.isOpen()) {
          return Promise.reject(
            new ApiError(
              ErrorCategory.SERVER,
              null,
              null,
              'Service temporarily unavailable (circuit breaker open)',
            )
          );
        }

        // Attach JWT token from localStorage
        const token = this.getAuthToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Generate Request ID for tracing
        const requestId = this.generateRequestId();
        if (config.headers) {
          config.headers['X-Request-ID'] = requestId;
        }

        // Attach Tenant ID if available
        const tenantId = this.getTenantId();
        if (tenantId && config.headers) {
          config.headers['X-Tenant-ID'] = tenantId;
        }

        // Attach CSRF token for state-changing requests
        // Only POST, PUT, PATCH, DELETE requests need CSRF protection (not GET)
        const method = config.method?.toUpperCase();
        csrfLog('[CSRF DEBUG] Interceptor called for request:', {
          method,
          url: config.url,
          timestamp: new Date().toISOString()
        });

        if (this.isStateChangingMethod(config.method)) {
          csrfLog('[CSRF DEBUG] State-changing method detected, attempting token extraction');

          const csrfToken = this.getCsrfToken();

          if (csrfToken && config.headers) {
            config.headers['X-CSRF-Token'] = csrfToken;

            csrfLog('[CSRF DEBUG] ✅ Token added to request:', {
              method,
              url: config.url,
              tokenLength: csrfToken.length,
              tokenFormat: csrfToken.includes('.') ? 'signed' : 'simple',
              tokenPreview: `${csrfToken.substring(0, 10)}...${csrfToken.substring(csrfToken.length - 10)}`,
              headerSet: true
            });
          } else {
            csrfError(`[CSRF ERROR] ❌ No CSRF token available for ${method} request:`, {
              url: config.url,
              cookiesAvailable: typeof document !== 'undefined' && document.cookie.length > 0,
              warning: 'Request will likely fail with 403 Forbidden'
            });
          }
        } else {
          csrfLog('[CSRF DEBUG] Read-only method, skipping token injection');
        }

        return config;
      },
      (error: Error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * Check if method is state-changing (requires CSRF token)
   */
  private isStateChangingMethod(method?: string): boolean {
    const stateChangingMethods = ['post', 'put', 'patch', 'delete'];
    return stateChangingMethods.includes(method?.toLowerCase() || '');
  }

  /**
   * Setup Response Interceptors
   */
  private setupResponseInterceptors(): void {
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // Record success for circuit breaker
        this.circuitBreaker.recordSuccess();
        return response.data;
      },
      async (error: AxiosError<ApiErrorResponse>) => {
        // Record failure for circuit breaker
        this.circuitBreaker.recordFailure();

        const categorizedError = this.categorizeError(error);

        // Handle CSRF token refresh for 403 errors
        if (categorizedError.category === ErrorCategory.AUTHORIZATION && error.response?.status === 403) {
          return this.handleCsrfError(error, categorizedError);
        }

        // Handle token refresh for 401 errors
        if (categorizedError.category === ErrorCategory.AUTHENTICATION) {
          return this.handleAuthenticationError(error, categorizedError);
        }

        return Promise.reject(categorizedError);
      }
    );
  }

  /**
   * Handle CSRF Errors with Token Refresh and Retry
   */
  private async handleCsrfError(
    error: AxiosError<ApiErrorResponse>,
    categorizedError: ApiError
  ): Promise<never> {
    const originalRequest = error.config as AxiosRequestConfig & { _csrfRetry?: boolean };

    // Prevent infinite retry loop
    if (originalRequest._csrfRetry) {
      csrfError('[CSRF ERROR] 403 error persists after token refresh - not retrying again');
      return Promise.reject(categorizedError);
    }

    // Only retry if this was a state-changing request that should have had a CSRF token
    const method = originalRequest.method?.toLowerCase();
    if (!method || !['post', 'put', 'patch', 'delete'].includes(method)) {
      // Not a state-changing request, don't retry
      return Promise.reject(categorizedError);
    }

    csrfError('[CSRF ERROR] 403 Forbidden - Attempting CSRF token refresh and retry', {
      method: originalRequest.method,
      url: originalRequest.url
    });

    originalRequest._csrfRetry = true;

    try {
      // Refetch CSRF token
      await this.fetchCsrfToken();

      // Retry the original request with new token
      const newToken = this.getCsrfToken();
      if (newToken && originalRequest.headers) {
        originalRequest.headers['X-CSRF-Token'] = newToken;
        csrfLog('[CSRF DEBUG] Retrying request with new token:', {
          method: originalRequest.method,
          url: originalRequest.url,
          tokenLength: newToken.length
        });
        return this.axiosInstance(originalRequest);
      } else {
        csrfError('[CSRF ERROR] Failed to obtain new CSRF token for retry');
        return Promise.reject(categorizedError);
      }
    } catch (refreshError) {
      csrfError('[CSRF ERROR] Token refresh failed:', refreshError);
      return Promise.reject(categorizedError);
    }
  }

  /**
   * Handle Authentication Errors with Token Refresh
   */
  private async handleAuthenticationError(
    error: AxiosError<ApiErrorResponse>,
    categorizedError: ApiError
  ): Promise<never> {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Prevent infinite retry loop
    if (originalRequest._retry) {
      this.clearAuthTokens();
      return Promise.reject(categorizedError);
    }

    originalRequest._retry = true;

    // Handle concurrent refresh requests
    if (isRefreshing) {
      return new Promise((resolve) => {
        subscribeTokenRefresh((token: string) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          resolve(this.axiosInstance(originalRequest));
        });
      });
    }

    isRefreshing = true;

    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // Call refresh endpoint
      const response = await axios.post<{ accessToken: string; refreshToken: string }>(
        `${this.config.baseURL}/auth/refresh`,
        { refreshToken }
      );

      const { accessToken, refreshToken: newRefreshToken } = response.data;

      // Update tokens
      this.setAuthToken(accessToken);
      this.setRefreshToken(newRefreshToken);

      // Notify waiting requests
      onTokenRefreshed(accessToken);

      // Retry original request
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      }

      return this.axiosInstance(originalRequest);
    } catch (refreshError) {
      // Refresh failed - clear tokens and reject
      this.clearAuthTokens();
      return Promise.reject(categorizedError);
    } finally {
      isRefreshing = false;
    }
  }

  /**
   * Categorize Axios Error
   */
  private categorizeError(error: AxiosError<ApiErrorResponse>): ApiError {
    // Network errors (no response)
    if (!error.response) {
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        return new ApiError(
          ErrorCategory.TIMEOUT,
          null,
          null,
          'Request timeout - please check your connection',
          error
        );
      }
      return new ApiError(
        ErrorCategory.NETWORK,
        null,
        null,
        'Network error - please check your connection',
        error
      );
    }

    const { status, data } = error.response;

    // Authentication errors (401)
    if (status === 401) {
      return new ApiError(
        ErrorCategory.AUTHENTICATION,
        status,
        data,
        data?.message || 'Authentication required',
        error
      );
    }

    // Authorization errors (403)
    if (status === 403) {
      return new ApiError(
        ErrorCategory.AUTHORIZATION,
        status,
        data,
        data?.message || 'Access forbidden',
        error
      );
    }

    // Validation errors (400, 422)
    if (status === 400 || status === 422) {
      return new ApiError(
        ErrorCategory.VALIDATION,
        status,
        data,
        data?.message || 'Validation error',
        error
      );
    }

    // Server errors (5xx)
    if (status >= 500) {
      return new ApiError(
        ErrorCategory.SERVER,
        status,
        data,
        data?.message || 'Server error - please try again later',
        error
      );
    }

    // Client errors (4xx)
    if (status >= 400) {
      return new ApiError(
        ErrorCategory.CLIENT,
        status,
        data,
        data?.message || 'Request error',
        error
      );
    }

    // Unknown error
    return new ApiError(
      ErrorCategory.UNKNOWN,
      status,
      data,
      data?.message || 'An unexpected error occurred',
      error
    );
  }

  /**
   * Retry Logic with Exponential Backoff
   */
  private async retryRequest<T>(
    requestFn: () => Promise<T>,
    attempt = 0
  ): Promise<T> {
    try {
      return await requestFn();
    } catch (error) {
      if (error instanceof ApiError && error.isRetryable() && attempt < this.config.retryAttempts) {
        const delay = this.config.retryDelay * Math.pow(2, attempt);
        await this.sleep(delay);
        return this.retryRequest(requestFn, attempt + 1);
      }
      throw error;
    }
  }

  /**
   * HTTP Methods with Retry Support
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.retryRequest(() => this.axiosInstance.get<T, T>(url, config));
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    // POST requests are only retried if idempotent
    return this.axiosInstance.post<T, T>(url, data, config);
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.axiosInstance.put<T, T>(url, data, config);
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.axiosInstance.patch<T, T>(url, data, config);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.axiosInstance.delete<T, T>(url, config);
  }

  /**
   * Token Management
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private setAuthToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  private setRefreshToken(token: string): void {
    localStorage.setItem('refresh_token', token);
  }

  private clearAuthTokens(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
  }

  private getTenantId(): string | null {
    return localStorage.getItem('quiz_commerce_tenant_id');
  }

  /**
   * CSRF Token Management
   *
   * @description
   * Implements dual-source token extraction following the Double Submit Cookie pattern:
   * 1. Primary: Extract token from HTTP cookie (set by backend with httpOnly=false)
   * 2. Fallback: Retrieve token from sessionStorage (legacy support)
   *
   * The cookie-based approach is preferred as it follows the standard Double Submit
   * Cookie pattern where the server sets a cookie and the client reads it and sends
   * the value as a request header.
   *
   * Supports both simple (32-char) and signed (193-char with HMAC) token formats.
   *
   * @returns CSRF token or null if not available
   */
  private getCsrfToken(): string | null {
    // Primary: Try to extract from cookie (Double Submit Cookie pattern)
    const cookieToken = getCookie('quiz-commerce.x-csrf-token');
    if (cookieToken) {
      // Validate token format
      const isSigned = cookieToken.includes('.');
      const expectedLength = isSigned ? 193 : 32;
      const isValidLength = cookieToken.length === expectedLength;

      csrfLog('[CSRF DEBUG] Token extracted from cookie:', {
        source: 'cookie',
        length: cookieToken.length,
        format: isSigned ? 'signed (token.signature)' : 'simple',
        valid: isValidLength
      });

      if (!isValidLength) {
        csrfError(`[CSRF WARN] ⚠️ Unexpected token length: ${cookieToken.length} (expected ${expectedLength})`);
      }

      return cookieToken;
    }

    // Fallback: Try sessionStorage (legacy support or SSR scenarios)
    const storageToken = sessionStorage.getItem('csrf_token');
    if (storageToken) {
      const isSigned = storageToken.includes('.');
      csrfLog('[CSRF DEBUG] Token extracted from sessionStorage (fallback):', {
        source: 'sessionStorage',
        length: storageToken.length,
        format: isSigned ? 'signed (token.signature)' : 'simple'
      });
      return storageToken;
    }

    // No token available
    csrfError('[CSRF WARN] ⚠️ No CSRF token available in cookie or sessionStorage');

    return null;
  }

  private setCsrfToken(token: string): void {
    sessionStorage.setItem('csrf_token', token);
  }

  /**
   * Fetch CSRF token from server
   *
   * @description
   * Fetches a fresh CSRF token from the backend. The backend will:
   * 1. Generate a new CSRF token
   * 2. Set it as an HTTP cookie (httpOnly=false)
   * 3. Return it in the JSON response
   *
   * This method stores the token in sessionStorage as a fallback, but the
   * primary mechanism is cookie-based extraction via getCsrfToken().
   *
   * Should be called on app initialization to ensure CSRF token is available
   * before any state-changing requests are made.
   *
   * @returns Promise that resolves when token is fetched (or fails silently)
   */
  async fetchCsrfToken(): Promise<void> {
    const maxRetries = 3;
    let attempt = 0;

    csrfLog('[CSRF DEBUG] fetchCsrfToken called');

    while (attempt < maxRetries) {
      try {
        const csrfUrl = `${this.config.baseURL}/csrf-token`;

        csrfLog(`[CSRF DEBUG] Fetching token from: ${csrfUrl} (attempt ${attempt + 1}/${maxRetries})`);

        const response = await axios.get<{ success: boolean; data: { csrfToken: string } }>(
          csrfUrl,
          {
            withCredentials: true,
            timeout: 5000
          }
        );

        csrfLog('[CSRF DEBUG] CSRF token fetch response:', {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers
        });

        // Store in sessionStorage as fallback
        this.setCsrfToken(response.data.data.csrfToken);

        csrfLog('[CSRF DEBUG] Token received in response:', {
          length: response.data.data.csrfToken.length,
          format: response.data.data.csrfToken.includes('.') ? 'signed' : 'simple',
          preview: `${response.data.data.csrfToken.substring(0, 10)}...${response.data.data.csrfToken.substring(response.data.data.csrfToken.length - 10)}`
        });
        csrfLog('[CSRF DEBUG] Token stored in sessionStorage as fallback');

        // Wait a moment for cookie to be set by browser
        await this.sleep(100);

        // Verify cookie was set
        const cookieToken = getCookie('quiz-commerce.x-csrf-token');
        if (cookieToken) {
          csrfLog('[CSRF DEBUG] ✅ Token successfully extracted from cookie:', {
            length: cookieToken.length,
            format: cookieToken.includes('.') ? 'signed' : 'simple',
            matches: cookieToken === response.data.data.csrfToken ? 'response' : 'different'
          });
          return; // Success!
        } else {
          csrfError('[CSRF WARN] ⚠️ Cookie not found after fetch - will use sessionStorage fallback', {
            documentCookie: typeof document !== 'undefined' ? document.cookie : '(no document)'
          });
          // Still return success as we have the token in sessionStorage
          return;
        }
      } catch (error) {
        attempt++;

        csrfError(`[CSRF ERROR] Token fetch attempt ${attempt} failed:`, error);

        if (attempt < maxRetries) {
          // Exponential backoff: 500ms, 1000ms, 2000ms
          const delay = 500 * Math.pow(2, attempt - 1);
          csrfLog(`[CSRF DEBUG] Retrying in ${delay}ms...`);
          await this.sleep(delay);
        } else {
          // All retries exhausted
          csrfError('[CSRF ERROR] ❌ All retry attempts exhausted. CSRF protection may not work.');
          // Silently fail in production - CSRF token fetch is optional during SSR/build/Lighthouse
          // In production with backend running, this should succeed
        }
      }
    }
  }

  /**
   * Utilities
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get underlying Axios instance for advanced usage
   */
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

/**
 * Singleton API Client Instance
 */
export const apiClient = new ApiClient();

/**
 * Export default instance
 */
export default apiClient;
